/**
 * AARKA Craft Triptych — Cinematic Scroll Cinema
 * Single pinned viewport. Scroll drives crossfade between panels.
 * Images do Ken Burns zoom, text staggers in, numbers sweep.
 * GSAP ScrollTrigger for pin + scrub. Snap at each panel.
 *
 * Scroll budget layout (3 panels example):
 *   [0.5vh dwell] [1.5vh panel-0→1] [1.5vh panel-1→2] [0.5vh dwell]
 *   Total = dwellVh*2 + scrollPerPanel*(panelCount-1) + scrollPerPanel
 */
if (!customElements.get('aarka-craft-triptych')) {
  customElements.define('aarka-craft-triptych', class extends HTMLElement {
    connectedCallback() {
      this.panelCount = parseInt(this.dataset.panelCount, 10) || 3;
      this.wrapper = this.querySelector('.act-wrapper');
      this.viewport = this.querySelector('.act-viewport');
      this.imageLayers = Array.from(this.querySelectorAll('.act-image-layer'));
      this.slides = Array.from(this.querySelectorAll('.act-slide'));
      this.digits = Array.from(this.querySelectorAll('.act-number__digit'));
      this.dots = Array.from(this.querySelectorAll('.act-dot'));
      this.currentIndex = 0;

      if (this.panelCount === 0) return;

      if (window.AARKA && window.AARKA.ready) {
        this.init();
      } else {
        document.addEventListener('aarka:scroll-ready', () => this.init(), { once: true });
      }
    }

    disconnectedCallback() {
      if (this._scrollTrigger) this._scrollTrigger.kill();
    }

    init() {
      var self = this;
      var ScrollTrigger = window.AARKA.ScrollTrigger;
      var vh = window.innerHeight;

      // ── Scroll budget ──
      // Dwell zone: extra scroll at start & end where first/last panel stays visible
      var dwellVh = 0.5;  // 50vh dwell at start and end
      // Per-panel transition runway
      var scrollPerPanel = vh * 1.5;
      // Total: dwell-start + (transitions between panels) + dwell-end
      var transitions = this.panelCount - 1;
      var totalScroll = (dwellVh * vh * 2) + (scrollPerPanel * transitions) + (scrollPerPanel);

      this.wrapper.style.height = totalScroll + 'px';

      // ── Compute progress thresholds ──
      // Progress ranges:
      //   [0 → dwellStart]                           = panel 0 (dwell)
      //   [dwellStart → dwellStart + transitionZone]  = panel 0→1→2... transitions
      //   [end - dwellEnd → 1]                        = last panel (dwell)
      var dwellStartProgress = (dwellVh * vh) / totalScroll;
      var dwellEndProgress = 1 - ((dwellVh * vh) / totalScroll);
      var transitionRange = dwellEndProgress - dwellStartProgress;

      // ── Build snap points — one per panel, mapped into the progress range ──
      var snapPoints = [];
      for (var i = 0; i < this.panelCount; i++) {
        if (this.panelCount === 1) {
          snapPoints.push(0.5);
        } else {
          // Map panel index into the transition zone, offset by dwell
          var panelProgress = dwellStartProgress + (i / (this.panelCount - 1)) * transitionRange;
          snapPoints.push(panelProgress);
        }
      }

      this._scrollTrigger = ScrollTrigger.create({
        trigger: this.wrapper,
        start: 'top top',
        end: 'bottom bottom',
        pin: this.viewport,
        scrub: 0.4,
        snap: {
          snapTo: snapPoints,
          duration: { min: 0.2, max: 0.5 },
          delay: 0.05,
          ease: 'power1.inOut',
          inertia: false
        },
        onUpdate: function(st) {
          var progress = st.progress;
          var newIndex;

          // Before dwell-start: show panel 0
          if (progress <= dwellStartProgress) {
            newIndex = 0;
          }
          // After dwell-end: show last panel
          else if (progress >= dwellEndProgress) {
            newIndex = self.panelCount - 1;
          }
          // In transition zone: map to panel index
          else {
            var localProgress = (progress - dwellStartProgress) / transitionRange;
            var exactIndex = localProgress * (self.panelCount - 1);
            newIndex = Math.round(exactIndex);
          }

          newIndex = Math.max(0, Math.min(newIndex, self.panelCount - 1));

          if (newIndex !== self.currentIndex) {
            self.setActive(newIndex);
          }
        }
      });

      // Activate first panel
      this.setActive(0);
    }

    setActive(index) {
      var prevIndex = this.currentIndex;
      this.currentIndex = index;

      // Crossfade images
      this.imageLayers.forEach(function(layer, i) {
        layer.classList.toggle('act-image-layer--active', i === index);
      });

      // Crossfade content slides
      this.slides.forEach(function(slide, i) {
        slide.classList.toggle('act-slide--active', i === index);
      });

      // Swap number
      this.digits.forEach(function(digit, i) {
        digit.classList.toggle('act-number__digit--active', i === index);
      });

      // Update dots
      this.dots.forEach(function(dot, i) {
        dot.classList.toggle('act-dot--active', i === index);
      });
    }
  });
}
